import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { EVENT_PRODUCTS, PRODUCTS_BY_ID } from '../../../shared/constants/api';
import * as api from '../../../shared/services/apiClient';
import type { ProductWithAddOns } from '../../product/types/product.types';

// ---------------------------------------------------------------------------
// Helper — exported so HomePage can re-use it for modal data (formatDateRange,
// getRawOptions, getIsMultiOption)
// ---------------------------------------------------------------------------
export const getAttribute = (product: ProductWithAddOns, attributeName: string): unknown => {
  const masterVariant =
    product?.masterVariant ||
    (
      product as unknown as {
        masterData?: { current?: { masterVariant?: unknown } };
      }
    )?.masterData?.current?.masterVariant;

  if (!(masterVariant as { attributes?: unknown[] })?.attributes) return null;
  const attr = (
    masterVariant as { attributes: Array<{ name: string; value?: unknown }> }
  ).attributes.find((a) => a.name === attributeName);
  return attr?.value ?? null;
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------
const fetchAddOns = async (product: ProductWithAddOns): Promise<ProductWithAddOns[]> => {
  const relatedAddOns = getAttribute(product, 'relatedAddons') as Array<{ id: string }> | null;
  if (!relatedAddOns || relatedAddOns.length === 0) return [];

  try {
    const addOnPromises = relatedAddOns.map(async (ref) => {
      const res = await api.get(PRODUCTS_BY_ID(ref.id));
      if (!res.ok) throw new Error(`Failed to fetch add-on ${ref.id}: ${res.status}`);
      return (await res.json()) as ProductWithAddOns;
    });
    return Promise.all(addOnPromises);
  } catch (err) {
    console.error('Failed to fetch add-ons:', err);
    return [];
  }
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export interface UseHomeProductsResult {
  products: ProductWithAddOns[];
  productsLoading: boolean;
  productsError: Error | null;
  /** Call to re-fetch (e.g. from a Retry button). */
  refetch: () => void;
}

export function useHomeProducts(): UseHomeProductsResult {
  const { eventKey } = useParams();

  const [products, setProducts] = useState<ProductWithAddOns[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState<Error | null>(null);

  const fetchProducts = useCallback(async () => {
    setProductsError(null);
    try {
      if (!eventKey) throw new Error('Event context is missing from the URL.');

      const res = await api.get(EVENT_PRODUCTS(eventKey), {
        limit: '50',
        expand: 'masterVariant.prices[*].discounted.discount',
      });

      if (!res.ok) throw new Error(`Failed to fetch products: ${res.status}`);
      const response = await res.json();

      if (response.results) {
        const productsWithAddOns = await Promise.all(
          response.results.map(async (product: ProductWithAddOns) => {
            const addOns = await fetchAddOns(product);
            return { ...product, addOns };
          })
        );
        const eventPasses = productsWithAddOns.filter((p: ProductWithAddOns) =>
          p.masterVariant?.attributes?.some(
            (a: { name: string }) => a.name === 'benefits' || a.name === 'startDate'
          )
        );
        setProducts(eventPasses);
      }
    } catch (error) {
      console.error(error);
      setProductsError(error instanceof Error ? error : new Error('Failed to load passes'));
    } finally {
      setProductsLoading(false);
    }
  }, [eventKey]);

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  return { products, productsLoading, productsError, refetch: fetchProducts };
}
